import React, { Component, memo, useMemo } from 'react';
import { DragSource, DropTarget } from 'react-dnd';
import ItemTypes from './ItemTypes';

const cardSource = {
  beginDrag(props) {
    return {
      itemType: ItemTypes.CARD,
      id: props.id,
      index: props.index,
    };
  },
};

const cardTarget = {
  drop(props, monitor, component) {
    if (!component) return;

    const item = monitor.getItem();
    const hoverIndex = props.index;
    const dragIndex = item.index;

    if ((props.data && props.data.isContainer) || item.itemType === ItemTypes.CARD) {
      return;
    }
    if (item.data && typeof item.setAsChild === 'function') {
      if (dragIndex === -1) {
        props.insertCard(item, hoverIndex, item.id);
      }
    }
  },
  hover(props, monitor, component) {
    const item = monitor.getItem();

    const dragIndex = item.index;
    const hoverIndex = props.index;

    if (item.itemType === ItemTypes.BOX && item.index === -1) return;
    if (props.data?.isContainer && !item.data?.isContainer) return;
    if (dragIndex === hoverIndex) return;

    if (dragIndex === -1) {
      if (props.data && props.data.isContainer) return;

      item.index = hoverIndex;
      const createdItem = item.onCreate(item.data);

      Object.assign(item, { data: createdItem });

      props.insertCard(createdItem, hoverIndex);
    }

    const hoverBoundingRect = component.ref.current.getBoundingClientRect();

    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

    const clientOffset = monitor.getClientOffset();

    const hoverClientY = clientOffset.y - hoverBoundingRect.top;

    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;

    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

    props.moveCard(dragIndex, hoverIndex);
    item.index = hoverIndex;
  },
};

export const SortableElement = memo(({ ComposedComponent, ...props }) => {
  const x = useMemo(
    () =>
      DropTarget([ItemTypes.CARD, ItemTypes.BOX], cardTarget, connect => ({
        connectDropTarget: connect.dropTarget(),
      }))(Card),
    [props],
  );

  const Component = useMemo(
    () =>
      DragSource(ItemTypes.CARD, cardSource, (connect, monitor) => {
        return {
          connectDragSource: connect.dragSource(),
          connectDragPreview: connect.dragPreview(),
          isDragging: monitor.isDragging(),
          ...props,
          ComposedComponent,
        };
      })(x),
    [props, x],
  );
  return <Component />;
});

const style = {
  marginBottom: '.5rem',
  backgroundColor: 'white',
  cursor: 'pointer',
};

class Card extends Component {
  constructor(p) {
    super(p);
    this.ref = React.createRef();
  }

  static defaultProps = { seq: -1 };

  handleMouseEnter() {
    const isProvider = !!this.props.data?.dependantsIds?.length;
    const isDependant = !!this.props.data?.providerId;

    if (!isProvider && !isDependant) return;

    if (isProvider) {
      const dependantsContainers = document.querySelectorAll(
        this.props.data.dependantsIds.map(id => `[data-anchor="${id}"]`).toString(),
      );

      dependantsContainers.forEach(container => {
        container.style.boxShadow = '0 0 0 0.2rem #ffc107';
      });
    } else if (isDependant) {
      const providerContainer = document.querySelector(
        `[data-anchor="${this.props.data.providerId}"]`,
      );
      if (providerContainer) {
        providerContainer.style.boxShadow = '0 0 0 0.2rem #28a745';
      }
    }
  }

  handleMouseLeave() {
    const isProvider = !!this.props.data?.dependantsIds?.length;
    const isDependant = !!this.props.data?.providerId;

    if (!isProvider && !isDependant) return;

    if (isProvider) {
      const dependantsContainers = document.querySelectorAll(
        this.props.data.dependantsIds.map(id => `[data-anchor="${id}"]`).toString(),
      );

      dependantsContainers.forEach(container => {
        container.style.boxShadow = '';
      });
    } else if (isDependant) {
      const providerContainer = document.querySelector(
        `[data-anchor="${this.props.data.providerId}"]`,
      );
      if (providerContainer) {
        providerContainer.style.boxShadow = '';
      }
    }
  }

  componentWillUnmount() {
    this.handleMouseLeave();
  }

  render() {
    const {
      isDragging,
      connectDragSource,
      connectDragPreview,
      connectDropTarget,
      ComposedComponent,
      ...props
    } = this.props;
    const opacity = isDragging ? 0 : 1;

    return connectDragPreview(
      connectDropTarget(
        <div
          ref={this.ref}
          style={{ borderRadius: 8 }}
          onMouseEnter={this.handleMouseEnter.bind(this)}
          onMouseLeave={this.handleMouseLeave.bind(this)}
        >
          <ComposedComponent {...props} style={isDragging ? { ...style, opacity } : style} />
        </div>,
      ),
    );
  }
}
